import { LoadingOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import { useEffect } from 'react';
import styled from 'styled-components';

import SorrySvg from '../../../../assets/icons/SorrySvg';
import theme from '../../../../assets/theme';
import { LicenseCoarseLevels, UserAccessBadge } from '../../../../hooks/useAccessBadge';
import { useBreakPoint } from '../../../../hooks/useBreakPoint';
import useConsumeGeneratorAsNewPresentation from '../../../../hooks/useConsumeGeneratorAsNewPresentation';
import { useDynamicLessonStateContext } from '../../../../hooks/useCreateLessonState';
import { useMetrics } from '../../../../hooks/useMetrics';
import { PaywallMetadata } from '../../../../hooks/usePaywallPrompt';
import useRouterMatch from '../../../../hooks/useRouterMatch';
import {
  LS_KEY_TEST_PREP_STATE,
  TestPrepAction,
  TestPrepState,
} from '../../../../hooks/useTestPrepState';
import { generatorFormStateToUserInputFormData } from '../../../../utils/aiGeneratorUtils';
import ShowLessonCreationProgressBar from '../../../_molecules/ProgressBar/ShowLessonCreationProgressBar';
import { Grade } from '../../../_molecules/SettingsModalDesigner/SettingsModalDesigner';
import { useGeneratorFormState } from '../../../_organisms/DesignerTabPane/PromptsTab/GeneratorForm';
import { TestPrepFloatingSelect } from '../components/FloatingSelect';
import {
  GradeTypeWithOrdinalIndicator,
  mapGradeToGradeWithOrdinalIndicator,
} from '../CreateFlow/components/GradeSelect';
import { ConsumerGenerator } from './consumer-generator';
import { useGetLessonVariants } from './TestPrep.Utils';
import { TestPrepGeneratorForm } from './TestPrepGeneratorForm';

export function TestPrepLastPage({
  setShowLoader,
  showLoader,
  availableLessonIds,
  availableGenerators,
  promptPaywall,
  isReady,
  userAccessBadge,
  isFetchingGenerators,
  testPrepState,
  selectedGenerator,
  dispatch,
  preFillGrade,
}: {
  setShowLoader: (d: boolean) => void;
  showLoader: boolean;
  availableLessonIds: string[] | undefined;
  availableGenerators: ConsumerGenerator[] | undefined;
  promptPaywall: (metadata?: PaywallMetadata | undefined) => void;
  isReady: boolean;
  userAccessBadge: UserAccessBadge | undefined;
  isFetchingGenerators: boolean;
  testPrepState: TestPrepState;
  selectedGenerator: ConsumerGenerator | undefined;
  dispatch: React.Dispatch<TestPrepAction>;
  preFillGrade?: Grade;
}) {
  const { tenantId, router } = useRouterMatch();
  const {
    selectedState,
    selectedTest,
    selectedTestGroup,
    lessonVariant,
    selectedQuestionType,
    isUpload,
  } = testPrepState;
  const {
    consumeGenerator,
    isError,
    isLoading,
    data,
    error,
  } = useConsumeGeneratorAsNewPresentation(tenantId, true);
  const metrics = useMetrics();
  const presetState = userAccessBadge?.organization?.region;
  const tenantSubjects = userAccessBadge?.user.profileInfo?.subjects;
  const tenantGrades = userAccessBadge?.user.profileInfo?.grades;
  const grade = useDynamicLessonStateContext(c => c.dynamicLessonState.grade);

  const selectedStandards = useDynamicLessonStateContext(
    c => c.dynamicLessonState.selectedStandards,
  );
  const prompt = useDynamicLessonStateContext(c => c.dynamicLessonState.prompt);

  const {
    handleInputChange,
    handleMultipleInputsChange,
    formState,
  } = useGeneratorFormState(selectedGenerator?.aiContent?.promptInputs);
  useEffect(() => {
    const gradeInput = selectedGenerator?.aiContent?.promptInputs?.find(p =>
      p.label.toLowerCase().includes('grade'),
    );
    const options = gradeInput?.constraints?.options;
    const isDefinitelyGradeInput =
      (options?.length ?? 0 > 0) &&
      options?.every(option => stringIsGradeWithOrdinalIndicator(option.value));

    if (gradeInput && preFillGrade && isDefinitelyGradeInput) {
      handleInputChange(gradeInput.id, mapGradeToGradeWithOrdinalIndicator(preFillGrade));
    }
    // only do this on the first render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isLoading || data) {
      setShowLoader(true);
      return;
    }

    setShowLoader(false);
    return;
  }, [isLoading, setShowLoader, data]);

  useEffect(() => {
    if (data) {
      metrics.logEvent('TestPrep.Generator.Consume.TestPrepLessonSuccessRedirect', {
        generatorId: selectedGenerator?.id,
        generatorName: selectedGenerator?.title,
        standard: selectedStandards?.map(s => s.id).join(','),
        subject: data.metadata?.subject,
        grade: grade,
        generatedLessonId: data.id,
        tenantSubjects: tenantSubjects,
        tenantGrades: tenantGrades,
        schoolState: presetState,
        prompt: [prompt || ''].join('\n\n'),
      });

      router.push(`/${tenantId}/lessons/${data.id}/edit?activities=true`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router, tenantId, data]);

  const isLargerThan850 = useBreakPoint(850);
  const { selectedCardId } = router.query;
  const handleGetLesson = () => {
    const [generatorId, variantId] = selectedGenerator?.id?.split('::') || [];
    metrics.logEvent('TestPrep.GetLessonButton.Clicked', {
      generatorId: variantId ? `${generatorId}::${variantId}` : generatorId,
      generatorName: variantId ? variantId : selectedGenerator?.title,
      testId: selectedTest,
      questionType: selectedQuestionType,
      lessonVariant: lessonVariant,
    });

    if (selectedGenerator?.id && selectedGenerator?.aiContent?.promptInputs) {
      const userInputFormData = generatorFormStateToUserInputFormData(
        formState,
        selectedGenerator.aiContent.promptInputs,
      );

      consumeGenerator({
        generatorId: variantId ? generatorId : selectedGenerator.id || '',
        userInputFormData,
        generatorName: variantId ? variantId : selectedGenerator.title || '',
        ...(variantId ? { variantId } : {}),
        generatorPlan: selectedGenerator?.aiContent?.generatorPlan,
        metadata: { selectedCardId: selectedCardId as string },
        onSuccessFunction: template => {
          localStorage.setItem(
            '@curipod_v1_recentGeneratorConsume',
            JSON.stringify({
              generatorId: selectedGenerator.id,
              formState,
            }),
          );

          localStorage.setItem(
            LS_KEY_TEST_PREP_STATE,
            JSON.stringify({
              selectedTest,
              selectedTestCategory: selectedTestGroup,
              selectedState,
            }),
          );

          // redirect to the designer
          router.push(
            `/${tenantId}/lessons/${template.id}/edit?generatorId=${selectedGenerator.id}&activities=true`,
          );
        },
      });
    }
  };
  const lessonVariantsAvailable = useGetLessonVariants({
    testId: selectedTest,
    questionTypeId: selectedQuestionType,
    generators: availableGenerators,
    userAccessBadge,
    isUpload,
  });

  const shouldDisplayVariantPicker = lessonVariantsAvailable.length > 1;
  const variantPickerLabel = getVariantPickerLabel(selectedQuestionType);

  useEffect(() => {
    if (!lessonVariantsAvailable[0]) return;
    if (lessonVariantsAvailable.length > 1) {
      //if the teacher is not on the school plan, we find the first free lesson variant and select that.
      if (userAccessBadge?.license.coarseLevel !== LicenseCoarseLevels.enterprise) {
        const freeLessonVariant = lessonVariantsAvailable.find(
          lessonVariant => lessonVariant.isEnterprise === false,
        );
        if (freeLessonVariant) {
          dispatch({ type: 'SET_LESSON_VARIANT', payload: freeLessonVariant.value });
          return;
        }
        return;
      }
      dispatch({ type: 'SET_LESSON_VARIANT', payload: lessonVariantsAvailable[0].value });
      return;
    }
  }, [
    availableGenerators,
    dispatch,
    isUpload,
    lessonVariantsAvailable,
    selectedQuestionType,
    selectedTest,
    userAccessBadge,
  ]);

  const lessonVariantOptions = useGetLessonVariants({
    testId: selectedTest,
    questionTypeId: selectedQuestionType,
    generators: availableGenerators,
    userAccessBadge,
    isUpload,
  });

  return (
    <>
      {showLoader && (
        <LoaderContainer>
          <ShowLessonCreationProgressBar />
        </LoaderContainer>
      )}
      {!showLoader && shouldDisplayVariantPicker && (
        <TestPrepFloatingSelect
          label={variantPickerLabel}
          width={isLargerThan850 ? undefined : '100%'}
          value={lessonVariant}
          options={lessonVariantOptions}
          onSelect={lessonVariant => {
            dispatch({ type: 'SET_LESSON_VARIANT', payload: lessonVariant });
          }}
          promptPaywall={promptPaywall}
          userAccessBadge={userAccessBadge}
          isReady={isReady}
        />
      )}
      {!showLoader &&
        availableLessonIds &&
        availableLessonIds[0] &&
        selectedGenerator &&
        !isFetchingGenerators && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              margin: '20px 0px',
              gap: 20,
            }}
          >
            <TestPrepGeneratorForm
              promptInputs={selectedGenerator?.aiContent?.promptInputs || []}
              isError={isError}
              error={error}
              promptInputSuggestions={
                selectedGenerator?.aiContent?.promptInputSuggestions || []
              }
              isPremiumGenerator={selectedGenerator?.aiContent?.isPremiumGenerator}
              generatorPlan={selectedGenerator?.aiContent?.generatorPlan}
              generatorId={selectedGenerator?.id}
              formState={formState}
              handleInputChange={handleInputChange}
              handleMultipleInputsChange={handleMultipleInputsChange}
              handleSubmit={handleGetLesson}
              promptPaywall={promptPaywall}
              isReady={isReady}
              userAccessBadge={userAccessBadge}
            />
          </div>
        )}
      {!showLoader && isFetchingGenerators && (
        <LoaderContainer>
          <Spin
            indicator={
              <LoadingOutlined
                style={{ marginTop: 30, fontSize: 60, color: theme.colors.orange }}
                spin
              />
            }
          />
        </LoaderContainer>
      )}
      {!selectedGenerator && availableLessonIds?.length === 1 && !isFetchingGenerators && (
        <div
          style={{
            margin: '20px 0px',
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <SorrySvg />
          <h2 style={{ textAlign: 'center' }}>
            Uh-oh! Something didn&apos;t go as planned.
          </h2>
          <p style={{ textAlign: 'center', maxWidth: 500 }}>
            We&apos;ve been notified of the issue, but in the meantime, you could try a
            different test. If you&apos;re still having trouble, just pop us a message
            using the chat in the bottom right, we&apos;re here to help!
          </p>
        </div>
      )}
    </>
  );
}

// the variant picker is deprecated; it looks like we can avoid having non-curify lesson variants
// by pushing options up to the question type level.
function getVariantPickerLabel(questionTypeId: string | undefined): string {
  return questionTypeId === 'ECR' || questionTypeId === 'PT' || questionTypeId === 'essay'
    ? 'Composition Type'
    : 'Passage Genre';
}

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

function stringIsGradeWithOrdinalIndicator(
  value: string,
): value is GradeTypeWithOrdinalIndicator {
  const grades: GradeTypeWithOrdinalIndicator[] = [
    'Pre-K',
    'Kindergarten',
    '1st',
    '2nd',
    '3rd',
    '4th',
    '5th',
    '6th',
    '7th',
    '8th',
    '9th',
    '10th',
    '11th',
    '12th',
  ];
  return grades.includes(value as GradeTypeWithOrdinalIndicator);
}
